/**
 * Base Skeleton component - Bauhaus-inspired loading placeholders
 */
import styles from './Skeleton.module.css';

type SkeletonVariant = 'text' | 'title' | 'thumbnail' | 'badge';

interface SkeletonProps {
  variant?: SkeletonVariant;
  width?: string;
  height?: string;
  className?: string;
}

export function Skeleton({
  variant = 'text',
  width,
  height,
  className
}: SkeletonProps) {
  return (
    <div
      className={`${styles.skeleton} ${styles[variant]} ${className || ''}`}
      style={{ width, height }}
      aria-hidden="true"
    />
  );
}

/**
 * Card skeleton - matches ProjectCard/ApplicationCard structure
 */
export function CardSkeleton({ className }: { className?: string }) {
  return (
    <div className={`${styles.card} ${className || ''}`} aria-hidden="true">
      <div className={styles.cardThumbnail} />
      <div className={styles.cardInfo}>
        <div className={styles.cardTitle} />
        <div className={styles.cardMeta} />
      </div>
    </div>
  );
}

/**
 * Row skeleton - for list items like versions
 */
export function RowSkeleton({ className }: { className?: string }) {
  return (
    <div className={`${styles.row} ${className || ''}`} aria-hidden="true">
      <div className={styles.rowIcon} />
      <div className={styles.rowContent}>
        <div className={styles.rowTitle} />
        <div className={styles.rowMeta} />
      </div>
    </div>
  );
}

/**
 * Grid of skeleton cards
 */
export function CardGridSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className={styles.grid} aria-busy="true" aria-label="Loading...">
      {Array.from({ length: count }).map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * List of skeleton rows
 */
export function RowListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className={styles.list} aria-busy="true" aria-label="Loading...">
      {Array.from({ length: count }).map((_, i) => (
        <RowSkeleton key={i} />
      ))}
    </div>
  );
}
